import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { EnrichedVisitor } from "@/types/database";
import VisitorsListView from "./visitors/VisitorsListView";
import { useAuth } from "@/components/AuthProvider";
import { useState } from "react";
import { useBlockedCompanies } from "@/hooks/useBlockedCompanies";

interface VisitorsTableProps {
  pixelId?: string;
}

const VisitorsTable = ({ pixelId }: VisitorsTableProps) => {
  const { session, user } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 25;
  
  const [userOrganization, setUserOrganization] = useState<string | undefined>(undefined);
  
  const { blockedCompanies } = useBlockedCompanies(userOrganization);

  const { data: enrichedVisitors, error: enrichedError, isLoading } = useQuery<EnrichedVisitor[]>({
    queryKey: ['dashboard-enriched-visitors', pixelId, session?.user?.id, blockedCompanies?.length],
    queryFn: async () => {
      try {
        console.log('Fetching leads for pixel:', pixelId);
        console.log('Current user:', user?.id, 'Email:', user?.email);
        
        if (!pixelId) {
          console.log('No pixel ID provided, skipping fetch');
          return [];
        }
        
        let query = supabase
          .from('enriched_visitors')
          .select('*, visitors!inner(*)')
          .order('created_at', { ascending: false });

        if (pixelId) {
          query = query.eq('visitors.pixel_id', pixelId);
        }

        const { data, error } = await query;
        
        if (error) {
          console.error('Supabase query error:', error);
          toast.error("Failed to fetch lead data");
          throw error;
        }
        
        console.log('Fetched leads:', data?.length || 0);
        
        if (data && data.length === 0) {
          const { count, error: countError } = await supabase
            .from('visitors')
            .select('id', { count: 'exact' })
            .eq('pixel_id', pixelId);
            
          console.log('Raw visitors count for this pixel:', count);
          if (countError) {
            console.error('Error counting visitors:', countError);
          }
        }
        
        let typedData: EnrichedVisitor[] = data?.map((item: any) => {
          const { visitors, ...enrichedData } = item;
          
          return {
            ...enrichedData,
            person_skills: typeof item.person_skills === 'string' 
              ? JSON.parse(item.person_skills) 
              : item.person_skills,
            person_interests: typeof item.person_interests === 'string'
              ? JSON.parse(item.person_interests)
              : item.person_interests,
            company_tags: typeof item.company_tags === 'string'
              ? JSON.parse(item.company_tags)
              : item.company_tags,
            page_url: visitors?.page_url || null
          };
        }) || [];
        
        if (blockedCompanies && blockedCompanies.length > 0) {
          console.log('Filtering out blocked companies, count:', blockedCompanies.length);
          
          typedData = typedData.filter(visitor => {
            if (!visitor.company_website && !visitor.company_name) return true;
            
            const isBlocked = blockedCompanies.some(blockedCompany => {
              if (visitor.company_website && blockedCompany.domain) {
                const visitorDomain = visitor.company_website.toLowerCase();
                const blockedDomain = blockedCompany.domain.toLowerCase();
                
                if (visitorDomain.includes(blockedDomain) || 
                    blockedDomain.includes(visitorDomain)) {
                  return true;
                }
              }
              
              if (visitor.company_name && blockedCompany.company_name) {
                const visitorCompany = visitor.company_name.toLowerCase();
                const blockedCompany_name = blockedCompany.company_name.toLowerCase();
                
                if (visitorCompany === blockedCompany_name) {
                  return true;
                }
              }
              
              return false;
            });
            
            return !isBlocked;
          });
          
          console.log('After filtering, visitor count:', typedData.length);
        }
        
        return typedData;
      } catch (error) {
        console.error('Error fetching leads:', error);
        toast.error("Failed to load leads. Please try refreshing the page.");
        throw error;
      }
    },
    enabled: !!pixelId && !!session?.user?.id,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  useQuery({
    queryKey: ['user-organization', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      try {
        const { data, error } = await supabase
          .from('organizations')
          .select('id')
          .eq('created_by', user.id)
          .single();
          
        if (error) {
          console.error("Error fetching user organization:", error);
          
          const { data: teamData, error: teamError } = await supabase
            .from('team_members')
            .select('organization_id')
            .eq('user_id', user.id)
            .eq('status', 'active')
            .not('organization_id', 'is', null)
            .single();
            
          if (teamError) {
            console.error("Error fetching team organization:", teamError);
            return null;
          }
          
          if (teamData?.organization_id) {
            console.log("Found organization from team member record:", teamData.organization_id);
            setUserOrganization(teamData.organization_id);
            return teamData.organization_id;
          }
          return null;
        }
        
        if (data?.id) {
          console.log("Found organization created by user:", data.id);
          setUserOrganization(data.id);
          return data.id;
        }
        
        return null;
      } catch (err) {
        console.error("Error in organization fetch:", err);
        return null;
      }
    },
    enabled: !!user?.id
  });

  if (enrichedError) {
    console.error('Data fetching error:', enrichedError);
    return (
      <div className="text-center py-8">
        <p className="text-red-500">Error loading lead data. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <VisitorsListView 
      visitors={enrichedVisitors || []} 
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      itemsPerPage={ITEMS_PER_PAGE}
      isLoading={isLoading}
    />
  );
};

export default VisitorsTable;
